import axios from "axios"
import { decodeHTML } from "entities"

const DEFAULT_LANGUAGE = "en"

async function fetchSupportedLanguages(apiKey) {
  const response = await axios.get(
    `https://translation.googleapis.com/language/translate/v2/languages?key=${apiKey}&target=${DEFAULT_LANGUAGE}`
  )
  const languages = response.data.data.languages
  const langMap = new Map()
  for (const lang of languages) {
    langMap.set(lang.language, lang.name)
  }
  return langMap;
}

function chunkArray(array, size) {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks;
}

function hasNoTranslateAncestor(node) {
  let currentNode = node
  while (currentNode) {
    if (
      currentNode.nodeType === Node.ELEMENT_NODE &&
      currentNode.classList.contains("notranslate")
    ) {
      return true;
    }
    currentNode = currentNode.parentNode;
  }
  return false;
}

function getTextNodes(node, accumulator = []) {
  if (hasNoTranslateAncestor(node)) {
    return accumulator;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    if (
      node.textContent?.trim() &&
      node.parentNode &&
      node.parentNode.nodeName !== "STYLE" &&
      node.parentNode.nodeName !== "SCRIPT"
    ) {
      accumulator.push(node);
    }
  } else {
    Array.from(node.childNodes).forEach(childNode => {
      getTextNodes(childNode, accumulator)
    })
  }
  return accumulator
}

function getPlaceholderElements(node, accumulator = []) {
  if (hasNoTranslateAncestor(node)) {
    return accumulator
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node
    if (
      element.hasAttribute("placeholder") &&
      element.getAttribute("placeholder")?.trim()
    ) {
      accumulator.push(element)
    }

    Array.from(node.childNodes).forEach(childNode => {
      getPlaceholderElements(childNode, accumulator)
    })
  }
  return accumulator
}

export class TranslateService {
  translationCache = new Map()
  static instance = null
  languages = new Map()
  originalTextContent = new Map()
  originalPlaceholderContent = new Map()
  isOriginalTextInitialized = false

  constructor(apiKey) {
    this.apiKey = apiKey
  }

  static getInstance(apiKey) {
    if (!TranslateService.instance) {
      TranslateService.instance = new TranslateService(apiKey)
    }
    return TranslateService.instance
  }

  async init() {
    this.languages = await fetchSupportedLanguages(this.apiKey)
    this.initOriginalTextContent()
  }

  /**
   * Initializes the original text content of all text nodes in the document.
   *
   * This method traverses the entire document body, finds all text nodes that do not have
   * an ancestor with the 'notranslate' class, and stores their original content in the
   * `originalTextContent` map. The map uses the text nodes as keys and their original content as values.
   *
   * The original content is used to reset the text nodes back to their original state before translation,
   * and to determine whether a text node needs to be translated.
   *
   * This method is called during the initialization of the TranslateService, and every time a mutation
   * that changes the document structure occurs.
   */
  initOriginalTextContent() {
    const textNodes = getTextNodes(document.body)
    const placeholderElements = getPlaceholderElements(document.body)

    for (const textNode of textNodes) {
      const parentElement = textNode.parentNode
      if (
        textNode.textContent &&
        (!parentElement?.hasAttribute("data-orig-content") ||
          document.documentElement.getAttribute("data-translated-language") ===
            DEFAULT_LANGUAGE)
      ) {
        const test = parentElement?.setAttribute("data-orig-content", textNode.textContent)
        this.originalTextContent.set(textNode, textNode.textContent)
      }
    }

    for (const element of placeholderElements) {
      const parentElement = element.parentNode
      if (
        element.getAttribute("placeholder") &&
        (!parentElement?.hasAttribute("data-orig-content") ||
          document.documentElement.getAttribute("data-translated-language") ===
            DEFAULT_LANGUAGE)
      ) {
        const originalContent = element.getAttribute("placeholder")
        const test = parentElement?.setAttribute("data-orig-content", originalContent)
        this.originalPlaceholderContent.set(element, originalContent)
      }
    }

    this.isOriginalTextInitialized = true
  }

  async translateTextNodes(node, targetLanguage, apiKey) {
    const textNodes = getTextNodes(node)
    const placeholderElements = getPlaceholderElements(node)

    const newNodesToTranslate = textNodes.filter(node => {
      const originalText = this.originalTextContent.get(node)
      if (originalText) {
        const translationsForText = this.translationCache.get(originalText)
        if (translationsForText && translationsForText.has(targetLanguage)) {
          node.textContent = translationsForText.get(targetLanguage) || null
          return false
        }
      }
      return true
    })
    if (newNodesToTranslate.length > 0) {
      const maxSegments = 128 // Google's limitation
      const textNodeChunks = chunkArray(newNodesToTranslate, maxSegments)

      for (const textNodeChunk of textNodeChunks) {
        try {
          const response = await axios.post(
            `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
            {
              q: textNodeChunk.map(textNode => textNode.textContent),
              source: DEFAULT_LANGUAGE,
              target: targetLanguage
            }
          )
          const translatedTexts = response.data.data.translations.map(
            translation => decodeHTML(translation.translatedText)
          )

          for (let i = 0; i < textNodeChunk.length; i++) {
            const originalText = this.originalTextContent.get(textNodeChunk[i])
            if (originalText) {
              let translationsForText = this.translationCache.get(originalText)
              if (!translationsForText) {
                translationsForText = new Map()
                this.translationCache.set(originalText, translationsForText)
              }
              translationsForText.set(targetLanguage, translatedTexts[i])
              if (translatedTexts[i]) {
                textNodeChunk[i].textContent = translatedTexts[i]
              }
            }
          }
        } catch (error) {
          console.error("Translation error for node:", textNodeChunk, error)
        }
      }
    }

    const newElementsToTranslate = placeholderElements.filter(element => {
      const originalText = this.originalPlaceholderContent.get(element)
      if (originalText) {
        const translationsForText = this.translationCache.get(originalText)
        if (translationsForText && translationsForText.has(targetLanguage)) {
          element.setAttribute(
            "placeholder",
            translationsForText.get(targetLanguage) || ""
          )
          return false
        }
      }
      return true
    })
    if (newElementsToTranslate.length > 0) {
      const maxSegments = 128 // Google's limitation
      const elementChunks = chunkArray(newElementsToTranslate, maxSegments)

      for (const elementChunk of elementChunks) {
        try {
          const response = await axios.post(
            `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
            {
              q: elementChunk.map(element =>
                element.getAttribute("placeholder")
              ),
              source: DEFAULT_LANGUAGE,
              target: targetLanguage
            }
          )
          const translatedTexts = response.data.data.translations.map(
            translation => translation.translatedText
          )

          for (let i = 0; i < elementChunk.length; i++) {
            const originalText = this.originalPlaceholderContent.get(
              elementChunk[i]
            )
            if (originalText) {
              let translationsForText = this.translationCache.get(originalText)
              if (!translationsForText) {
                translationsForText = new Map()
                this.translationCache.set(originalText, translationsForText)
              }
              translationsForText.set(targetLanguage, translatedTexts[i])
              if (translatedTexts[i]) {
                elementChunk[i].setAttribute("placeholder", translatedTexts[i])
              }
            }
          }
        } catch (error) {
          console.error(
            "Translation error for placeholder:",
            elementChunk,
            error
          )
        }
      }
    }
  }

  resetLanguage() {
    if (this.isOriginalTextInitialized) {
      const textNodes = getTextNodes(document.body)
      for (const textNode of textNodes) {
        const orgText = this.originalTextContent.get(textNode)
        if (orgText) {
          textNode.textContent = orgText
        }
      }
      const placeholderElements = getPlaceholderElements(document.body)
      for (const element of placeholderElements) {
        const orgText = this.originalPlaceholderContent.get(element)
        if (orgText) {
          element.setAttribute("placeholder", orgText)
        }
      }
    }
  }

  async translatePage(targetLanguage) {
    const currentTranslatedLanguage = document.documentElement.getAttribute(
      "data-translated-language"
    )
    if (
      currentTranslatedLanguage !== DEFAULT_LANGUAGE ||
      targetLanguage === DEFAULT_LANGUAGE
    ) {
      this.resetLanguage()
    }
    if (targetLanguage !== DEFAULT_LANGUAGE) {
      if (!this.apiKey) {
        throw new Error("Google Translate API key is not set.")
      }
      return await this.translateTextNodes(
        document.body,
        targetLanguage,
        this.apiKey
      ).then(r => {
        this.setTranslatedLanguage(targetLanguage)
        return r
      })
    }
    this.setTranslatedLanguage(targetLanguage)
  }

  setTranslatedLanguage(language) {
    document.documentElement.setAttribute("data-translated-language", language)
  }

  async translate(language) {
    await this.translatePage(language)
  }
}
